import { useCircle } from '@rhg/hooks/use-circle';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryClient, queryKeys, useQuery } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useActiveCircleId } from '../use-active-circle';
import { programQueryOptions } from '../use-program';

const programsQueryOptions = {
    queryKey: function ({
        items,
        circleId,
    }: {
        items: { programIds: string[]; packClass: string }[];
        circleId: number;
    }) {
        return [queryKeys.programs, circleId, items];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        items,
        circleId,
        circleAuthorizationToken,
        rHGApp,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        items: { programIds: string[]; packClass: string }[];
        circleId: number;
        circleAuthorizationToken: string;
        rHGApp: string;
    }) {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getRecipientPrograms({
            circleAuthorizationToken,
            mediaPackProgramsRequest: {
                items,
            },
            recipientId: circleId,
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data;
    },
    queryOptions: function ({
        items,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    }: {
        items: { programIds: string[]; packClass: string }[];
        isAuthenticated: boolean;
        circleId: number;
        circleAuthorizationToken: string;
    }) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                items?.length
            ),
        };
    },
};

export function usePrograms(
    items: { programIds: string[]; packClass: string }[]
) {
    const { activeCircleId: circleId } = useActiveCircleId();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = programsQueryOptions;
    const rHGApp = getRhgAppHeader();

    const filteredItems = items.filter((item) => item.programIds.length > 0);

    const options = queryOptions({
        items: filteredItems,
        isAuthenticated,
        circleId,
        circleAuthorizationToken,
    });

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey({ items: filteredItems, circleId }),
        queryFn: async () => {
            const response = await queryFn({
                getAccessTokenSilently,
                items: filteredItems,
                circleId,
                circleAuthorizationToken,
                rHGApp,
            });
            // populate cache for each programs
            //
            // The response order is not guaranteed to be the same as the input order
            // so we need to find the index of each response entry in the input items
            const inputProgramIdKeys = filteredItems.map((item) =>
                item.programIds.map((programId) => programId).join(',')
            );
            response?.items?.forEach((item) => {
                const responseProgramIdKey = item.programs
                    .map((program) => program.id)
                    .join(',');
                const inputItemIdex = inputProgramIdKeys.findIndex(
                    (programIdKey) => programIdKey === responseProgramIdKey
                );
                if (inputItemIdex === -1) {
                    return;
                }
                const packClass = filteredItems[inputItemIdex].packClass;
                item.programs.forEach((program) => {
                    const key = programQueryOptions.queryKey({
                        programId: program.id,
                        packClass,
                        circleId,
                    });
                    queryClient.setQueryData(key, program);
                });
            });
            return response;
        },
        ...options,
    });

    return {
        programs: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}
